import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CommonHeadingNBtns from "../../common-components/CommonHeadingNBtns";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import { commonPostPutFormData } from "../../../redux/reducers/commonApiSlice";
import {
  addWhatsNew,
  updateWhatsNew,
  whatNewUploadConfig,
} from "../../../data/ApiEndpoints";
import { BASE_URL } from "../../../data/BaseUrl";

// --- media rules -------------------------------------------------------------
// Defaults — overridden at runtime by GET /whatNew/upload-config.
const DEFAULT_RULE = {
  maxSizeMb: 5, // default per-card cap
  uploadCeilingMb: 50, // hard server ceiling — the max a per-card cap can be
  aspectRatio: { width: 9, height: 16 },
  tolerance: 0.05,
};

// When true, media_width / media_height / max_size_mb are sent in the payload.
// NOTE: the backend what_new table must have these columns or the save will
// fail with `column "media_width" of relation "what_new" does not exist`
// (same class of error as the video column).
const SEND_MEDIA_META = true;

const withinSize = (file, maxMb) => file.size <= maxMb * 1024 * 1024;
const isVideo = (file) => !!file && file.type?.startsWith("video/");
const isVideoUrl = (url = "") => /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(url);

// Read intrinsic dimensions to warn (not block) on aspect mismatch.
const readDimensions = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const done = (w, h) => {
      URL.revokeObjectURL(url);
      resolve({ w, h });
    };
    if (isVideo(file)) {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => done(v.videoWidth, v.videoHeight);
      v.onerror = () => done(0, 0);
      v.src = url;
    } else {
      const img = new Image();
      img.onload = () => done(img.naturalWidth, img.naturalHeight);
      img.onerror = () => done(0, 0);
      img.src = url;
    }
  });

// Capture a frame from a video file and return it as an image File (JPEG).
// `atSeconds` defaults to 1s to skip black/fade-in opening frames. Resolves
// null if the browser can't decode the codec — caller falls back gracefully.
const captureVideoThumbnail = (file, atSeconds = 1) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;

    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(result);
    };

    // Safety net: never hang the caller (Save awaits this) if a codec never
    // fires seeked/error. Give up and fall back after a few seconds.
    const timer = setTimeout(() => finish(null), 5000);

    const draw = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => finish(blob ? new File([blob], "video-thumb.jpg", { type: "image/jpeg" }) : null),
          "image/jpeg",
          0.8
        );
      } catch (err) {
        finish(null);
      }
    };

    video.onloadeddata = () => {
      // clamp the seek time so it never exceeds the clip length
      video.currentTime = Math.min(atSeconds, (video.duration || 1) / 2);
    };
    video.onseeked = draw;
    video.onerror = () => finish(null);

    video.src = url;
  });

const WhatsNewForm = ({ userInfoData, closeModal, callFunction, isEdit, rowData }) => {
  const dispatch = useDispatch();
  const { postPutLoadingFormData } = useSelector((state) => state.common);

  // Dynamic upload rules from the backend (size limit, allowed types, aspect).
  const [rule, setRule] = useState(DEFAULT_RULE);
  const maxMb = rule?.maxSizeMb || DEFAULT_RULE.maxSizeMb;
  const ceilingMb = rule?.uploadCeilingMb || DEFAULT_RULE.uploadCeilingMb;

  useEffect(() => {
    let active = true;
    fetch(`${BASE_URL}${whatNewUploadConfig}`)
      .then((r) => r.json())
      .then((j) => {
        if (active && j?.data) setRule((prev) => ({ ...prev, ...j.data }));
      })
      .catch(() => {
        /* keep DEFAULT_RULE on failure */
      });
    return () => {
      active = false;
    };
  }, []);

  const formInitials = {
    name: isEdit && rowData?.name ? rowData.name : "",
    description: isEdit && rowData?.description ? rowData.description : "",
    heading: isEdit && rowData?.heading ? rowData.heading : "",
    button_name: isEdit && rowData?.button_name ? rowData.button_name : "",
    button_link: isEdit && rowData?.button_link ? rowData.button_link : "",
    display_order: isEdit && rowData?.display_order ? rowData.display_order : "",
    media_width: isEdit && rowData?.media_width ? rowData.media_width : 9,
    media_height: isEdit && rowData?.media_height ? rowData.media_height : 16,
    max_size_mb: isEdit && rowData?.max_size_mb ? rowData.max_size_mb : DEFAULT_RULE.maxSizeMb,
  };

  const [formValues, setFormValues] = useState(formInitials);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(isEdit && rowData?.icon ? rowData.icon : "");

  // One media slot — holds either an image OR a video.
  const [mediaFile, setMediaFile] = useState(null);
  const existingMediaUrl = isEdit ? rowData?.video || rowData?.image || "" : "";
  const [mediaPreview, setMediaPreview] = useState(existingMediaUrl);
  const [mediaIsVideo, setMediaIsVideo] = useState(!!(isEdit && rowData?.video));

  // Auto-generated thumbnail extracted from an uploaded video (used as the
  // icon when the admin hasn't picked one of their own).
  const [autoThumbFile, setAutoThumbFile] = useState(null);

  const [formErrors, setFormErrors] = useState({});

  const onChangeInput = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const warnAspect = async (file) => {
    const { w, h } = await readDimensions(file);
    if (!w || !h) return;
    const target = Number(formValues.media_width) / Number(formValues.media_height);
    const actual = w / h;
    if (Math.abs(actual - target) / target > (rule.tolerance || 0.05)) {
      toast.warn(
        `Media is ${w}×${h} (ratio ${actual.toFixed(2)}), not ${formValues.media_width}:${formValues.media_height}. It may show black bars in-app.`
      );
    }
  };

  // Single handler — accepts an image or a video and routes it accordingly.
  const handleMediaChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const cap = Math.min(Number(formValues.max_size_mb) || maxMb, ceilingMb);
    if (!withinSize(file, cap)) {
      toast.error(`File too large. Maximum allowed size is ${cap} MB`);
      e.target.value = "";
      return;
    }
    setMediaFile(file);
    const videoPicked = isVideo(file);
    setMediaIsVideo(videoPicked);
    setMediaPreview(URL.createObjectURL(file));
    warnAspect(file);

    // Auto-extract a thumbnail frame when a video is uploaded.
    if (videoPicked) {
      const thumb = await captureVideoThumbnail(file, 1);
      if (thumb) {
        setAutoThumbFile(thumb);
        // surface it in the icon preview only if the admin hasn't set one
        if (!iconFile && !iconPreview) setIconPreview(URL.createObjectURL(thumb));
        toast.info("Thumbnail auto-generated from video.");
      } else {
        setAutoThumbFile(null);
      }
    } else {
      setAutoThumbFile(null);
    }
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!withinSize(file, maxMb)) {
      toast.error(`File too large. Maximum allowed size is ${maxMb} MB`);
      e.target.value = "";
      return;
    }
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const validateForm = () => {
    const errors = {};
    if (!formValues.name.trim()) errors.name = "Name is required";
    if (!formValues.description.trim()) errors.description = "Description is required";
    return errors;
  };

  const onSubmit = () => {
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length !== 0) return;

    const handleConfirm = async () => {
      const formData = new FormData();
      if (isEdit) formData.append("uid", rowData?.uid);

      if (formValues.name) formData.append("name", formValues.name);
      if (formValues.description) formData.append("description", formValues.description);
      if (formValues.heading) formData.append("heading", formValues.heading);
      if (formValues.button_name) formData.append("button_name", formValues.button_name);
      if (formValues.button_link) formData.append("button_link", formValues.button_link);
      if (formValues.display_order) formData.append("display_order", formValues.display_order);

      if (SEND_MEDIA_META) {
        if (formValues.media_width) formData.append("media_width", formValues.media_width);
        if (formValues.media_height) formData.append("media_height", formValues.media_height);
        if (formValues.max_size_mb) formData.append("max_size_mb", formValues.max_size_mb);
      }

      // Route the single media file to the correct backend column.
      if (mediaFile) {
        formData.append(mediaIsVideo ? "video" : "image", mediaFile);
      }

      // Icon: a manually-picked icon wins; otherwise auto-extract a frame from
      // the uploaded video. We generate here (awaited) in addition to on
      // file-pick, so a quick Save still gets a thumbnail even when the on-pick
      // extraction hasn't finished yet (race) or transiently failed.
      if (iconFile) {
        formData.append("icon", iconFile);
      } else if (mediaIsVideo && mediaFile) {
        const thumb = autoThumbFile || (await captureVideoThumbnail(mediaFile, 1));
        if (thumb) formData.append("icon", thumb);
      }

      try {
        const method = isEdit ? "PUT" : "POST";
        const endpoint = isEdit ? updateWhatsNew : addWhatsNew;
        const result = await dispatch(
          commonPostPutFormData({ method, endpoint, requestBody: formData, token: userInfoData?.token })
        ).unwrap();

        if (result?.status) {
          toast.success(result?.message || "Success!");
          closeModal();
          callFunction();
        } else {
          toast.error(result?.message || "Something went wrong!");
        }
      } catch (error) {
        toast.error(error?.message || "Failed to save");
      }
    };

    showConfirmAlert(
      handleConfirm,
      `Are you sure!`,
      `${isEdit ? "Update" : "Add"} Entry`,
      `${isEdit ? "Update" : "Add"}`
    );
  };

  const previewMaxHeight = 260;
  const previewAspect = `${formValues.media_width || 9} / ${formValues.media_height || 16}`;
  const showVideo = mediaFile ? mediaIsVideo : isVideoUrl(mediaPreview);

  return (
    <div className="createNewCTOModalContainer mealsModalContainer addMealModal">
      <CommonHeadingNBtns
        title={`${isEdit ? "Update" : "Add New"} — What's New`}
        onClose={closeModal}
        onClick={onSubmit}
        isLoading={postPutLoadingFormData}
      />
      <form className="formContainer">
        <div className="formInputsGrid twoGridSystem">
          <div className="formInputBox">
            <label htmlFor="name" className="formInputLabel">
              Name <span className="required">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter feature name"
              className={`formInput ${formErrors?.name ? "formError" : ""}`}
              name="name"
              value={formValues.name}
              onChange={onChangeInput}
            />
          </div>

          <div className="formInputBox">
            <label htmlFor="heading" className="formInputLabel">
              Heading
            </label>
            <input
              type="text"
              placeholder="e.g. New Feature, Coming Soon"
              className="formInput"
              name="heading"
              value={formValues.heading}
              onChange={onChangeInput}
            />
          </div>

          <div className="formInputBox">
            <label htmlFor="button_name" className="formInputLabel">
              Button Name
            </label>
            <input
              type="text"
              placeholder="e.g. Try Now, Learn More"
              className="formInput"
              name="button_name"
              value={formValues.button_name}
              onChange={onChangeInput}
            />
          </div>

          <div className="formInputBox">
            <label htmlFor="button_link" className="formInputLabel">
              Button Link
            </label>
            <input
              type="url"
              placeholder="https://..."
              className="formInput"
              name="button_link"
              value={formValues.button_link}
              onChange={onChangeInput}
            />
          </div>

          <div className="formInputBox">
            <label htmlFor="display_order" className="formInputLabel">
              Display Order
            </label>
            <input
              type="number"
              placeholder="1"
              className="formInput"
              name="display_order"
              value={formValues.display_order}
              onChange={onChangeInput}
              min={1}
            />
          </div>

          <div className="formInputBox">
            <label htmlFor="max_size_mb" className="formInputLabel">
              Max Video Size (MB, up to {ceilingMb})
            </label>
            <input
              type="number"
              placeholder="5"
              className="formInput"
              name="max_size_mb"
              value={formValues.max_size_mb}
              onChange={onChangeInput}
              min={1}
              max={ceilingMb}
            />
          </div>

          <div className="formInputBox">
            <label htmlFor="media_width" className="formInputLabel">
              Aspect Width
            </label>
            <input type="number" placeholder="9" className="formInput" name="media_width" value={formValues.media_width} onChange={onChangeInput} min={1} />
          </div>

          <div className="formInputBox">
            <label htmlFor="media_height" className="formInputLabel">
              Aspect Height
            </label>
            <input type="number" placeholder="16" className="formInput" name="media_height" value={formValues.media_height} onChange={onChangeInput} min={1} />
          </div>
        </div>

        <div className="formInputsGrid">
          <div className="formInputBox span2">
            <label htmlFor="description" className="formInputLabel">
              Description <span className="required">*</span>
            </label>
            <textarea
              name="description"
              className={`formInput ${formErrors?.description ? "formError" : ""}`}
              placeholder="Enter description"
              value={formValues.description}
              onChange={onChangeInput}
              rows={4}
            ></textarea>
          </div>
        </div>

        <div className="formInputsGrid twoGridSystem">
          {/* Single media slot — image OR video */}
          <div className="formInputBox">
            <label className="formInputLabel">Banner Media (Image or MP4/GIF) — max {formValues.max_size_mb || maxMb} MB</label>
            <div className="whatsNewUploadBox">
              <input
                type="file"
                accept="image/*,video/mp4"
                onChange={handleMediaChange}
                className="whatsNewFileInput"
                id="whatsNewMedia"
              />
              <label htmlFor="whatsNewMedia" className="whatsNewUploadLabel">
                {mediaPreview ? (
                  showVideo ? (
                    <video src={mediaPreview} className="whatsNewPreviewVideo" muted playsInline controls preload="metadata" />
                  ) : (
                    <img src={mediaPreview} alt="preview" className="whatsNewPreviewImg" />
                  )
                ) : (
                  <span className="whatsNewUploadPlaceholder">Upload Image or Video</span>
                )}
              </label>
            </div>
          </div>

          <div className="formInputBox">
            <label className="formInputLabel">Icon</label>
            <div className="whatsNewUploadBox">
              <input type="file" accept="image/*" onChange={handleIconChange} className="whatsNewFileInput" id="whatsNewIcon" />
              <label htmlFor="whatsNewIcon" className="whatsNewUploadLabel">
                {iconPreview ? (
                  <img src={iconPreview} alt="icon preview" className="whatsNewPreviewImg" />
                ) : (
                  <span className="whatsNewUploadPlaceholder">Upload Icon</span>
                )}
              </label>
            </div>
          </div>
        </div>

        {/* Live preview at the chosen aspect + size */}
        <div className="formInputsGrid twoGridSystem">
          <div className="formInputBox">
            <label className="formInputLabel">
              Preview ({formValues.media_width}:{formValues.media_height} · max {formValues.max_size_mb}MB)
            </label>
            <div className="whatsNewPreviewStage">
              {mediaPreview ? (
                <div className="whatsNewPreviewFrame" style={{ aspectRatio: previewAspect, maxHeight: previewMaxHeight }}>
                  {showVideo ? (
                    <video src={mediaPreview} muted playsInline controls preload="metadata" />
                  ) : (
                    <img src={mediaPreview} alt="card preview" />
                  )}
                </div>
              ) : (
                <span className="whatsNewUploadPlaceholder">Add media to preview</span>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default WhatsNewForm;
